import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LogIn, Users, Shield, Clock, Map, ChevronRight, ChevronLeft, Check, Lock, HelpCircle, ArrowLeft } from "lucide-react";
import { MapContainer, TileLayer, Polygon, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";

// Kort-preview komponent med rigtigt Leaflet-kort
function MapPreviewModal({ eventId, areas, workZones, onClose, eventLocation }) {
  // Beregn center af alle zoner
  const calculateCenter = () => {
    const allCorners = [];
    areas.forEach(a => {
      if (a.corners && a.corners.length > 0) {
        a.corners.forEach(c => allCorners.push([c.lat, c.lng]));
      }
    });
    workZones.forEach(wz => {
      if (wz.corners && wz.corners.length > 0) {
        wz.corners.forEach(c => allCorners.push([c.lat, c.lng]));
      }
    });
    
    if (allCorners.length === 0) {
      // Brug event location eller default
      return eventLocation ? [eventLocation.lat, eventLocation.lng] : [55.4, 10.4];
    }
    
    const avgLat = allCorners.reduce((sum, c) => sum + c[0], 0) / allCorners.length;
    const avgLng = allCorners.reduce((sum, c) => sum + c[1], 0) / allCorners.length;
    return [avgLat, avgLng];
  };

  const center = calculateCenter();
  const hasZones = areas.some(a => a.corners?.length > 0) || workZones.some(wz => wz.corners?.length > 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90">
      <div className="bg-zinc-900 border border-zinc-700 rounded-lg w-full max-w-5xl h-[85vh] mx-4 flex flex-col">
        <div className="p-4 border-b border-zinc-700 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold">Kort over arbejdszoner</h2>
            <p className="text-sm text-muted-foreground">Orienter dig før du vælger område og arbejdszone</p>
          </div>
          <Button variant="outline" onClick={onClose}>
            Luk kort
          </Button>
        </div>
        
        <div className="flex-1 flex">
          {/* Kort */}
          <div className="flex-1 relative">
            {hasZones ? (
              <MapContainer
                center={center}
                zoom={15}
                style={{ height: "100%", width: "100%" }}
                scrollWheelZoom={true}
              >
                <TileLayer
                  attribution='&copy; <a href="https://carto.com/">CartoDB</a>'
                  url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                />
                
                {/* Tegn områder (lilla/indigo) */}
                {areas.filter(a => a.corners?.length > 0).map(area => (
                  <Polygon
                    key={area.id}
                    positions={area.corners.map(c => [c.lat, c.lng])}
                    pathOptions={{
                      color: '#6366f1',
                      fillColor: '#6366f1',
                      fillOpacity: 0.15,
                      weight: 3
                    }}
                  >
                    <Tooltip permanent direction="center" className="zone-label">
                      <span style={{ fontWeight: 'bold', color: '#6366f1' }}>{area.name}</span>
                    </Tooltip>
                  </Polygon>
                ))}
                
                {/* Tegn arbejdszoner (grøn) */}
                {workZones.filter(wz => wz.corners?.length > 0).map(wz => (
                  <Polygon
                    key={wz.id}
                    positions={wz.corners.map(c => [c.lat, c.lng])}
                    pathOptions={{
                      color: '#22c55e',
                      fillColor: '#22c55e',
                      fillOpacity: 0.25,
                      weight: 2
                    }}
                  >
                    <Tooltip permanent direction="center" className="zone-label">
                      <span style={{ fontWeight: 'bold', color: '#22c55e' }}>
                        {wz.label || wz.name}
                      </span>
                    </Tooltip>
                  </Polygon>
                ))}
              </MapContainer>
            ) : (
              <div className="h-full flex items-center justify-center bg-zinc-800">
                <div className="text-center p-8">
                  <Map className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">
                    Ingen zoner med koordinater er oprettet endnu.
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Admin skal først tegne områder og arbejdszoner på kortet.
                  </p>
                </div>
              </div>
            )}
          </div>
          
          {/* Legende/oversigt i siden */}
          <div className="w-64 border-l border-zinc-700 p-4 overflow-auto">
            <h3 className="font-bold text-sm mb-3 text-muted-foreground uppercase tracking-wider">Legende</h3>
            
            <div className="space-y-4">
              {/* Områder */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-4 h-4 rounded bg-indigo-500/50 border-2 border-indigo-500"></div>
                  <span className="text-sm font-medium">Områder</span>
                </div>
                <div className="space-y-1 ml-6">
                  {areas.map(area => (
                    <div key={area.id} className="text-xs text-muted-foreground">
                      {area.name}
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Arbejdszoner */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-4 h-4 rounded bg-green-500/50 border-2 border-green-500"></div>
                  <span className="text-sm font-medium">Arbejdszoner</span>
                </div>
                <div className="space-y-1 ml-6">
                  {workZones.map(wz => (
                    <div key={wz.id} className="text-xs text-muted-foreground">
                      {wz.label || wz.name}
                      {wz.name !== wz.label && ` (${wz.name})`}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="mt-6 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <p className="text-xs text-yellow-400">
                💡 <strong>Tip:</strong> Vælg samme arbejdszone som dine venner for at arbejde sammen!
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Login({ onLogin }) {
  // Login type: "admin" (standard login) eller "volunteer" (frivillig)
  const [loginType, setLoginType] = useState("volunteer");
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("frivillig");
  const [sweeperTeamId, setSweeperTeamId] = useState("");
  const [sweeperTeams, setSweeperTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Frivillig login - step-baseret flow
  const [volunteerStep, setVolunteerStep] = useState(1); // 1: Event+Email, 2: Funktion, 3: Område/Arbejdszone, 4: Password
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState("");
  
  // Event-logo til dynamisk baggrund
  const [eventLogoUrl, setEventLogoUrl] = useState(null);
  const [platformSettings, setPlatformSettings] = useState(null);
  
  // URL parameter for event pre-selection (fra QR-kode)
  const [searchParams] = useSearchParams();
  
  // Funktioner og valg
  const [mainRoles, setMainRoles] = useState([]);
  const [selectedMainRoleId, setSelectedMainRoleId] = useState("");
  const [selectedMainRole, setSelectedMainRole] = useState(null);
  
  // Områder og arbejdszoner
  const [areas, setAreas] = useState([]);
  const [workZones, setWorkZones] = useState([]);
  const [selectedAreaId, setSelectedAreaId] = useState("");
  const [selectedWorkZoneId, setSelectedWorkZoneId] = useState("");
  
  // Kort-preview
  const [showMapPreview, setShowMapPreview] = useState(false);
  const [eventLocation, setEventLocation] = useState(null);
  
  // Låste felter fra admin
  const [preflightData, setPreflightData] = useState(null);
  const [mainRoleLocked, setMainRoleLocked] = useState(false);
  const [areaLocked, setAreaLocked] = useState(false);
  const [workZoneLocked, setWorkZoneLocked] = useState(false);
  
  // Leder-status
  const [isLeader, setIsLeader] = useState(false);
  const [leaderConfirmed, setLeaderConfirmed] = useState(false);
  
  // Venteliste state
  const [showWaitlistDialog, setShowWaitlistDialog] = useState(false);
  const [waitlistRole, setWaitlistRole] = useState(null);

  // Legacy: Tilgængelige roller (bruges til bagudkompatibilitet)
  const [availableRoles, setAvailableRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState("");
  const [zoneCode, setZoneCode] = useState("");

  // Hent events
  useEffect(() => {
    if (loginType === "volunteer") {
      axios.get(`${API}/events`)
        .then(res => {
          setEvents(res.data);
          // Pre-select event fra QR-kode URL-parameter
          const eventParam = searchParams.get('event');
          if (eventParam && res.data.some(e => e.id === eventParam)) {
            setSelectedEventId(eventParam);
          }
        })
        .catch(() => setEvents([]));
    }
  }, [loginType, searchParams]);

  // Hent platform settings
  useEffect(() => {
    axios.get(`${API}/platform/settings`)
      .then(res => setPlatformSettings(res.data))
      .catch(() => {});
  }, []);

  // Hent event-logo når event skiftes
  useEffect(() => {
    if (selectedEventId) {
      const selectedEvent = events.find(e => e.id === selectedEventId);
      if (selectedEvent?.logo_url) {
        setEventLogoUrl(`${API.replace('/api', '')}${selectedEvent.logo_url}`);
      } else {
        setEventLogoUrl(null);
      }
    } else {
      setEventLogoUrl(null);
    }
  }, [selectedEventId, events]);

  // Hent preflight data når email og event er valgt
  const fetchPreflight = async () => {
    if (!selectedEventId || !email) return;
    
    try {
      const res = await axios.post(`${API}/auth/volunteer-preflight`, {
        email,
        event_id: selectedEventId
      });
      
      setPreflightData(res.data);
      
      if (res.data.found) {
        // Sæt forhåndsvalgte værdier
        if (res.data.main_role_id) {
          setSelectedMainRoleId(res.data.main_role_id);
        }
        if (res.data.area_id) {
          setSelectedAreaId(res.data.area_id);
        }
        if (res.data.work_zone_id) {
          setSelectedWorkZoneId(res.data.work_zone_id);
        }
        
        // Sæt låse-status
        setMainRoleLocked(res.data.main_role_locked || false);
        setAreaLocked(res.data.area_locked || false);
        setWorkZoneLocked(res.data.work_zone_locked || false);
      } else {
        // Reset låse-status
        setMainRoleLocked(false);
        setAreaLocked(false);
        setWorkZoneLocked(false);
      }
    } catch (error) {
      console.error("Preflight error:", error);
      // Ignorer fejl - brugeren kan stadig logge ind
    }
  };

  // Hent funktioner når event er valgt
  useEffect(() => {
    if (loginType === "volunteer" && selectedEventId) {
      axios.get(`${API}/events/${selectedEventId}/main-roles`)
        .then(res => setMainRoles(res.data))
        .catch(() => setMainRoles([]));
        
      // Hent også områder og arbejdszoner
      axios.get(`${API}/areas?event_id=${selectedEventId}`)
        .then(res => setAreas(res.data))
        .catch(() => setAreas([]));
        
      axios.get(`${API}/work-zones?event_id=${selectedEventId}`)
        .then(res => setWorkZones(res.data))
        .catch(() => setWorkZones([]));
      
      // Hent event-lokation til kort-centrering
      axios.get(`${API}/events`)
        .then(res => {
          const event = res.data.find(e => e.id === selectedEventId);
          if (event?.location) {
            setEventLocation(event.location);
          }
        })
        .catch(() => {});
    }
  }, [loginType, selectedEventId]);

  // Opdater selectedMainRole når funktion vælges
  useEffect(() => {
    if (selectedMainRoleId) {
      const role = mainRoles.find(r => r.id === selectedMainRoleId);
      setSelectedMainRole(role);
      // Reset område/arbejdszone valg
      setSelectedAreaId("");
      setSelectedWorkZoneId("");
    } else {
      setSelectedMainRole(null);
    }
  }, [selectedMainRoleId, mainRoles]);

  // Hent sweeper-hold til registrering
  useEffect(() => {
    if (isRegister) {
      axios.get(`${API}/sweeper-teams`)
        .then(res => setSweeperTeams(res.data))
        .catch(() => setSweeperTeams([]));
    }
  }, [isRegister]);

  // Beregn om vi kan gå videre til næste step
  const canProceedToStep2 = selectedEventId && email;
  const canProceedToStep3 = selectedMainRoleId || mainRoleLocked;
  // Område og arbejdszone er teknisk set valgfrit - man kan altid gå videre
  const canProceedToStep4 = true;
  
  // Filter arbejdszoner baseret på valgt område
  const filteredWorkZones = selectedAreaId 
    ? workZones.filter(wz => wz.area_id === selectedAreaId)
    : workZones;

  const handleNextStep = async () => {
    if (volunteerStep === 1 && canProceedToStep2) {
      // Hent preflight data og valider email FØR vi går videre
      let preflight = null;
      try {
        const res = await axios.post(`${API}/auth/volunteer-preflight`, {
          email,
          event_id: selectedEventId
        });
        preflight = res.data;
        setPreflightData(res.data);
        
        // VIGTIG: Tjek om emailen findes i systemet
        if (!res.data.found) {
          const eventName = events.find(e => e.id === selectedEventId)?.name || 'dette event';
          toast.error(`Din email er ikke registreret til ${eventName}. Kontakt din koordinator.`);
          return; // Stop her - gå ikke videre
        }
        
        // Email fundet - sæt forhåndsvalgte værdier
        if (res.data.main_role_id) {
          setSelectedMainRoleId(res.data.main_role_id);
        }
        if (res.data.area_id) {
          setSelectedAreaId(res.data.area_id);
        }
        if (res.data.work_zone_id) {
          setSelectedWorkZoneId(res.data.work_zone_id);
        }
        
        // Sæt låse-status
        setMainRoleLocked(res.data.main_role_locked || false);
        setAreaLocked(res.data.area_locked || false);
        setWorkZoneLocked(res.data.work_zone_locked || false);
        
        // Tjek om brugeren er leder
        setIsLeader(res.data.is_leader || false);
        
      } catch (error) {
        console.error("Preflight error:", error);
        toast.error("Kunne ikke validere din email. Prøv igen.");
        return; // Stop ved fejl
      }
      
      // GÆSTE-FLOW (Plan A): gæster har ingen funktion/område — spring direkte til password-trin
      if (preflight && preflight.user_type === 'guest') {
        setVolunteerStep(4);
        return;
      }
      
      // NY LOGIK: Vis ALTID step 2 (funktion-trinnet), uanset låse-status
      // Dette sikrer, at brugeren ser hvad der er tildelt, selv om det er låst
      setVolunteerStep(2);
      
    } else if (volunteerStep === 2 && canProceedToStep3) {
      // Hvis funktionen ikke har områder/arbejdszoner
      if (!selectedMainRole?.has_areas && !selectedMainRole?.has_work_zones) {
        setVolunteerStep(4);
      } else {
        // NY LOGIK: Vis ALTID step 3, uanset låse-status
        // Brugeren skal se hvad der er forhåndstildelt
        setVolunteerStep(3);
      }
    } else if (volunteerStep === 3 && canProceedToStep4) {
      setVolunteerStep(4);
    }
  };

  const handlePrevStep = () => {
    if (volunteerStep === 4) {
      // Gæster har intet funktion/område-trin — gå direkte tilbage til step 1
      if (preflightData?.user_type === 'guest') {
        setVolunteerStep(1);
        return;
      }
      // Gå tilbage til step 3 eller 2 afhængigt af funktion
      if (selectedMainRole?.has_areas || selectedMainRole?.has_work_zones) {
        setVolunteerStep(3);
      } else {
        setVolunteerStep(2);
      }
    } else if (volunteerStep === 3) {
      // Gå altid tilbage til step 2 (funktion-valg)
      setVolunteerStep(2);
    } else if (volunteerStep === 2) {
      setVolunteerStep(1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (loginType === "volunteer") {
        // Gæste-flow (Plan A): brug standard /auth/login da gæster ikke har funktion/område
        if (preflightData?.user_type === 'guest') {
          if (!selectedEventId) {
            toast.error("Vælg et event");
            setLoading(false);
            return;
          }
          const response = await axios.post(`${API}/auth/login`, { email, password });
          // Sikkerhedstjek: gæsten skal være tilknyttet det valgte event
          const assigned = response.data.user?.assigned_event_ids || [];
          if (assigned.length && !assigned.includes(selectedEventId)) {
            toast.error("Denne gæsteadgang gælder ikke for det valgte event.");
            setLoading(false);
            return;
          }
          toast.success(`Velkommen ${response.data.user.name}!`);
          localStorage.setItem('selectedEventId', selectedEventId);
          const selectedEvent = events.find(e => e.id === selectedEventId);
          if (selectedEvent) {
            localStorage.setItem('selectedEventName', selectedEvent.name);
          }
          onLogin(response.data.token, response.data.user);
          return;
        }

        // Nyt frivillig login med funktion-system
        if (!selectedEventId) {
          toast.error("Vælg et event");
          setLoading(false);
          return;
        }
        
        // Byg payload med det nye system
        const payload = {
          email,
          password,
          event_id: selectedEventId,
          // Nyt system
          main_role_id: selectedMainRoleId || null,
          area_id: selectedAreaId || null,
          work_zone_id: selectedWorkZoneId || null,
          // Legacy (bevares for bagudkompatibilitet)
          zone_code: zoneCode || null,
          selected_role: selectedMainRole?.name || null
        };
        
        const response = await axios.post(`${API}/auth/volunteer-login`, payload);
        toast.success(`Velkommen ${response.data.user.name}!`);
        
        // Gem event info
        localStorage.setItem('selectedEventId', selectedEventId);
        const selectedEvent = events.find(e => e.id === selectedEventId);
        if (selectedEvent) {
          localStorage.setItem('selectedEventName', selectedEvent.name);
        }
        
        // Gem også valg til næste login
        localStorage.setItem('lastMainRoleId', selectedMainRoleId);
        localStorage.setItem('lastAreaId', selectedAreaId);
        localStorage.setItem('lastWorkZoneId', selectedWorkZoneId);
        
        onLogin(response.data.token, response.data.user);
      } else {
        // Standard admin/medarbejder login
        const endpoint = isRegister ? "/auth/register" : "/auth/login";
        const payload = isRegister 
          ? { 
              email, 
              password, 
              name, 
              role,
              sweeper_team_id: role === 'sweeper' ? sweeperTeamId : null
            }
          : { email, password };

        const response = await axios.post(`${API}${endpoint}`, payload);
        toast.success(isRegister ? "Bruger oprettet!" : "Logget ind!");
        onLogin(response.data.token, response.data.user);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fejl ved login");
    } finally {
      setLoading(false);
    }
  };

  // Reset volunteer flow når loginType ændres
  useEffect(() => {
    if (loginType === "volunteer") {
      setVolunteerStep(1);
      // Prøv at hente gemte valg
      const lastMainRoleId = localStorage.getItem('lastMainRoleId');
      const lastAreaId = localStorage.getItem('lastAreaId');
      const lastWorkZoneId = localStorage.getItem('lastWorkZoneId');
      
      if (lastMainRoleId) setSelectedMainRoleId(lastMainRoleId);
      if (lastAreaId) setSelectedAreaId(lastAreaId);
      if (lastWorkZoneId) setSelectedWorkZoneId(lastWorkZoneId);
    }
  }, [loginType]);

  // Render step indicator
  const renderStepIndicator = () => {
    const steps = [
      { num: 1, label: "Event" },
      { num: 2, label: "Funktion" },
      { num: 3, label: "Område" },
      { num: 4, label: "Login" }
    ];
    
    // Hvis funktionen ikke har områder, fjern step 3
    const visibleSteps = selectedMainRole && !selectedMainRole.has_areas && !selectedMainRole.has_work_zones
      ? steps.filter(s => s.num !== 3)
      : steps;
    
    return (
      <div className="flex items-center justify-center gap-2 mb-6">
        {visibleSteps.map((step, idx) => {
          const isActive = volunteerStep === step.num;
          const isCompleted = volunteerStep > step.num;
          
          return (
            <div key={step.num} className="flex items-center">
              <div className={`
                w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold
                ${isActive ? 'bg-primary text-primary-foreground' : ''}
                ${isCompleted ? 'bg-green-500 text-white' : ''}
                ${!isActive && !isCompleted ? 'bg-zinc-700 text-zinc-400' : ''}
              `}>
                {isCompleted ? <Check className="h-4 w-4" /> : step.num}
              </div>
              {idx < visibleSteps.length - 1 && (
                <div className={`w-8 h-0.5 mx-1 ${isCompleted ? 'bg-green-500' : 'bg-zinc-700'}`} />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
         style={{background: '#F0EBE3'}}>
      {/* Flowing vine SVG — same as landing page */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 1440 900" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="loginVine" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#88C057" stopOpacity="0.22" />
            <stop offset="50%" stopColor="#5B8C2A" stopOpacity="0.16" />
            <stop offset="100%" stopColor="#88C057" stopOpacity="0.12" />
          </linearGradient>
          <linearGradient id="loginVine2" x1="1" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#6BA33E" stopOpacity="0.10" />
            <stop offset="100%" stopColor="#5B8C2A" stopOpacity="0.07" />
          </linearGradient>
        </defs>
        <path d="M 1500 -50 C 1200 100, 900 250, 800 400 C 650 600, 900 650, 700 800 C 500 1000, 200 900, 0 950 L 0 700 C 200 650, 450 750, 600 600 C 800 400, 550 350, 700 200 C 850 50, 1100 -50, 1500 -50 Z" fill="url(#loginVine)" />
        <path d="M -50 -50 C 200 50, 400 200, 350 400 C 280 650, 50 600, -50 800 L -50 550 C 50 400, 200 450, 250 300 C 300 150, 100 50, -50 -50 Z" fill="url(#loginVine2)" />
      </svg>
      <div className="absolute inset-0 bg-gradient-to-b from-[#F0EBE3]/50 via-transparent to-[#F0EBE3]/50 pointer-events-none"></div>
      
      {/* Kort Preview Modal */}
      {showMapPreview && (
        <MapPreviewModal 
          eventId={selectedEventId}
          areas={areas}
          workZones={workZones}
          eventLocation={eventLocation}
          onClose={() => setShowMapPreview(false)}
        />
      )}
      
      <div className="relative w-full max-w-md z-[2]">
        {/* Tilbage til forsiden */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-sm text-[#6B5D4D] hover:text-[#2C2416] transition-colors mb-4"
          data-testid="login-back-to-landing"
        >
          <ArrowLeft className="h-4 w-4" />
          Tilbage til forsiden
        </Link>
        
        <div className="bg-[#2C2416]/95 backdrop-blur-md border border-[#5B8C2A]/20 rounded-xl shadow-2xl overflow-hidden">
          
          {/* Vandmærke event-logo direkte bag loginfelterne */}
          {eventLogoUrl && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0 overflow-hidden">
              <img
                src={eventLogoUrl}
                alt=""
                className="w-[98%] max-w-none h-auto opacity-[0.20] select-none"
                style={{ filter: 'brightness(1.4)' }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
          )}
          
          <div className="relative z-10 p-8 pb-4">
            {/* Hjælp-knap */}
            <a 
              href="/brugsvejledning" 
              className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-muted/30 transition-colors text-muted-foreground hover:text-foreground"
              title="Brugsvejledning"
              data-testid="login-help-btn"
            >
              <HelpCircle className="h-4 w-4" />
            </a>
            
            {/* Header med event-logo til højre */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="flex items-center gap-3">
                  {!eventLogoUrl && <LogIn className="h-8 w-8 text-primary" />}
                  <h1 className="text-3xl font-bold font-heading uppercase tracking-tight">
                    {platformSettings?.platform_name || 'Resovia'}
                  </h1>
                </div>
                {selectedEventId && events.find(e => e.id === selectedEventId) && (
                  <p className="text-xs text-primary font-mono mt-1">
                    {events.find(e => e.id === selectedEventId)?.name}
                  </p>
                )}
              </div>
              {eventLogoUrl && (
                <img src={eventLogoUrl} alt="Event" className="h-14 w-14 object-contain rounded-lg border border-border/30" />
              )}
            </div>
          
          <p className="text-muted-foreground text-sm mb-6">
            Festival Logistics Management System
          </p>

          {/* Login Type Tabs */}
          {!isRegister && (
            <div className="flex gap-2 mb-6">
              <button
                type="button"
                onClick={() => setLoginType("volunteer")}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                  loginType === "volunteer" 
                    ? "bg-primary text-primary-foreground" 
                    : "bg-muted/50 text-muted-foreground hover:bg-muted"
                }`}
                data-testid="login-type-volunteer"
              >
                <LogIn className="h-4 w-4" />
                Login
              </button>
              <button
                type="button"
                onClick={() => setLoginType("admin")}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                  loginType === "admin" 
                    ? "bg-primary text-primary-foreground" 
                    : "bg-muted/50 text-muted-foreground hover:bg-muted"
                }`}
                data-testid="login-type-admin"
              >
                <Shield className="h-4 w-4" />
                Admin
              </button>
            </div>
          )}

          {/* Step Indicator for Volunteer Login */}
          {loginType === "volunteer" && !isRegister && renderStepIndicator()}

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            
            {/* ===== VOLUNTEER STEP 1: Event + Email ===== */}
            {loginType === "volunteer" && !isRegister && volunteerStep === 1 && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="event" className="text-xs uppercase tracking-wider font-mono">
                    Vælg Event
                  </Label>
                  <select
                    id="event"
                    data-testid="volunteer-event-select"
                    value={selectedEventId}
                    onChange={(e) => setSelectedEventId(e.target.value)}
                    required
                    className="flex h-10 w-full rounded-md border border-input bg-zinc-800 px-3 py-2 text-sm font-mono focus:border-primary focus:ring-1 focus:ring-primary text-white"
                  >
                    <option value="">Vælg event...</option>
                    {events.map(event => (
                      <option key={event.id} value={event.id}>{event.name}</option>
                    ))}
                  </select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-xs uppercase tracking-wider font-mono">
                    Din Email
                  </Label>
                  <Input
                    id="email"
                    data-testid="volunteer-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="din@email.dk"
                    className="bg-zinc-800 border-input focus:border-primary h-10 font-mono"
                  />
                  <p className="text-xs text-muted-foreground">
                    Din email skal være registreret i eventets frivilligliste
                  </p>
                </div>
                
                <Button
                  type="button"
                  onClick={handleNextStep}
                  disabled={!canProceedToStep2}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-10"
                  data-testid="volunteer-next-step1"
                >
                  Næste: Vælg funktion
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              </>
            )}

            {/* ===== VOLUNTEER STEP 2: Funktion ===== */}
            {loginType === "volunteer" && !isRegister && volunteerStep === 2 && (
              <>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs uppercase tracking-wider font-mono">
                      {mainRoleLocked ? 'Din tildelte funktion' : 'Vælg din funktion'}
                    </Label>
                    {mainRoleLocked && (
                      <span className="flex items-center gap-1 text-xs text-red-400">
                        <Lock className="h-3 w-3" /> Låst af admin
                      </span>
                    )}
                  </div>
                  
                  {mainRoleLocked && selectedMainRoleId ? (
                    // Vis kun den låste rolle med resolved navn
                    <div className="p-4 rounded-lg border border-primary bg-primary/10">
                      <div className="font-bold">{selectedMainRole?.name || preflightData?.main_role_name || 'Indlæser...'}</div>
                      {(selectedMainRole?.description) && (
                        <div className="text-sm text-muted-foreground mt-1">{selectedMainRole.description}</div>
                      )}
                      <p className="text-xs text-muted-foreground mt-2">
                        Denne rolle er valgt af din koordinator og kan ikke ændres.
                      </p>
                    </div>
                  ) : mainRoles.length === 0 ? (
                    <p className="text-sm text-yellow-500 py-4">
                      Ingen funktioner oprettet for dette event. Kontakt admin.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {mainRoles.map(mr => (
                        <button
                          key={mr.id}
                          type="button"
                          onClick={() => !mainRoleLocked && setSelectedMainRoleId(mr.id)}
                          disabled={mainRoleLocked}
                          className={`w-full p-4 rounded-lg border text-left transition-all ${
                            selectedMainRoleId === mr.id
                              ? 'border-primary bg-primary/10'
                              : 'border-zinc-700 bg-zinc-800 hover:border-zinc-600'
                          } ${mainRoleLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                          data-testid={`main-role-${mr.id}`}
                        >
                          <div className="font-bold">{mr.name}</div>
                          {mr.description && (
                            <div className="text-sm text-muted-foreground mt-1">{mr.description}</div>
                          )}
                          <div className="flex gap-2 mt-2">
                            {mr.has_areas && (
                              <span className="text-xs px-2 py-0.5 bg-indigo-500/20 text-indigo-400 rounded">
                                Flere områder
                              </span>
                            )}
                            {mr.has_work_zones && (
                              <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded">
                                Arbejdszoner
                              </span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Leder-bekræftelse */}
                {isLeader && selectedMainRoleId && (
                  <div className="p-4 rounded-lg border border-yellow-500/50 bg-yellow-500/10">
                    <div className="flex items-center gap-3 mb-3">
                      <Shield className="h-6 w-6 text-yellow-500" />
                      <div>
                        <p className="font-bold text-yellow-500">Du er leder!</p>
                        <p className="text-sm text-muted-foreground">
                          Du har leder-rettigheder for dette hold
                        </p>
                      </div>
                    </div>
                    
                    <label className="flex items-center gap-3 cursor-pointer p-3 rounded bg-zinc-800 border border-zinc-700 hover:border-yellow-500/50 transition-colors">
                      <input
                        type="checkbox"
                        checked={leaderConfirmed}
                        onChange={(e) => setLeaderConfirmed(e.target.checked)}
                        className="w-5 h-5 rounded accent-yellow-500"
                      />
                      <div>
                        <p className="font-medium">Jeg logger ind som leder for {selectedMainRole?.name || 'dette hold'}</p>
                        <p className="text-xs text-muted-foreground">
                          Du får adgang til at administrere frivillige i dit hold
                        </p>
                      </div>
                    </label>
                  </div>
                )}
                
                {/* Vis "Se kort" knap for Sweeper-rolle eller roller med arbejdszoner */}
                {selectedMainRole && (selectedMainRole.has_work_zones || selectedMainRole.name.toLowerCase().includes('sweep')) && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowMapPreview(true)}
                    className="w-full"
                    data-testid="show-map-preview"
                  >
                    <Map className="h-4 w-4 mr-2" />
                    Se kort over områder og arbejdszoner
                  </Button>
                )}
                
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handlePrevStep}
                    className="flex-1"
                  >
                    <ChevronLeft className="h-4 w-4 mr-2" />
                    Tilbage
                  </Button>
                  <Button
                    type="button"
                    onClick={handleNextStep}
                    disabled={!canProceedToStep3}
                    className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
                    data-testid="volunteer-next-step2"
                  >
                    Næste
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </>
            )}

            {/* ===== VOLUNTEER STEP 3: Område + Arbejdszone ===== */}
            {loginType === "volunteer" && !isRegister && volunteerStep === 3 && (
              <>
                {/* Info-boks hvis alle felter er låst */}
                {areaLocked && workZoneLocked && selectedAreaId && selectedWorkZoneId && (
                  <div className="p-4 rounded-lg border border-primary bg-primary/10 mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Lock className="h-4 w-4 text-primary" />
                      <span className="font-bold">Din koordinator har valgt:</span>
                    </div>
                    <div className="space-y-1 text-sm">
                      <div>
                        <span className="text-muted-foreground">Område: </span>
                        <span className="font-medium">{areas.find(a => a.id === selectedAreaId)?.name || preflightData?.area_name || 'Ukendt'}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Arbejdszone: </span>
                        <span className="font-medium">{workZones.find(wz => wz.id === selectedWorkZoneId)?.label || workZones.find(wz => wz.id === selectedWorkZoneId)?.name || preflightData?.work_zone_name || 'Ukendt'}</span>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-3">
                      Disse valg er foretaget af din koordinator og kan ikke ændres.
                    </p>
                  </div>
                )}
                
                {/* Område valg */}
                {selectedMainRole?.has_areas && !(areaLocked && workZoneLocked && selectedAreaId && selectedWorkZoneId) && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs uppercase tracking-wider font-mono">
                        {areaLocked ? 'Dit tildelte område' : 'Vælg område'}
                      </Label>
                      {areaLocked && (
                        <span className="flex items-center gap-1 text-xs text-red-400">
                          <Lock className="h-3 w-3" /> Låst
                        </span>
                      )}
                    </div>
                    {areaLocked && selectedAreaId ? (
                      <div className="p-3 rounded-lg border border-primary bg-primary/10">
                        <div className="font-medium">{areas.find(a => a.id === selectedAreaId)?.name || 'Ukendt'}</div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Dette område er valgt af din koordinator.
                        </p>
                      </div>
                    ) : (
                      <select
                        value={selectedAreaId}
                        onChange={(e) => {
                          if (!areaLocked) {
                            setSelectedAreaId(e.target.value);
                            setSelectedWorkZoneId(""); // Reset arbejdszone
                          }
                        }}
                        disabled={areaLocked}
                        className={`flex h-10 w-full rounded-md border border-input bg-zinc-800 px-3 py-2 text-sm font-mono focus:border-primary text-white ${areaLocked ? 'opacity-70 cursor-not-allowed' : ''}`}
                        data-testid="volunteer-area-select"
                      >
                        <option value="">Vælg område...</option>
                        {areas.map(area => (
                          <option key={area.id} value={area.id}>
                            {area.name}
                            {area.description && ` (${area.description})`}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}
                
                {/* Arbejdszone valg */}
                {selectedMainRole?.has_work_zones && (selectedAreaId || !selectedMainRole?.has_areas) && !(areaLocked && workZoneLocked && selectedAreaId && selectedWorkZoneId) && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs uppercase tracking-wider font-mono">
                        {workZoneLocked ? 'Din tildelte arbejdszone' : 'Vælg arbejdszone'}
                      </Label>
                      {workZoneLocked && (
                        <span className="flex items-center gap-1 text-xs text-red-400">
                          <Lock className="h-3 w-3" /> Låst
                        </span>
                      )}
                    </div>
                    {workZoneLocked && selectedWorkZoneId ? (
                      <div className="p-3 rounded-lg border border-primary bg-primary/10">
                        <div className="font-medium">{workZones.find(wz => wz.id === selectedWorkZoneId)?.label || workZones.find(wz => wz.id === selectedWorkZoneId)?.name || 'Ukendt'}</div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Denne arbejdszone er valgt af din koordinator.
                        </p>
                      </div>
                    ) : (
                      <>
                        <select
                          value={selectedWorkZoneId}
                          onChange={(e) => !workZoneLocked && setSelectedWorkZoneId(e.target.value)}
                          disabled={workZoneLocked}
                          className={`flex h-10 w-full rounded-md border border-input bg-zinc-800 px-3 py-2 text-sm font-mono focus:border-primary text-white ${workZoneLocked ? 'opacity-70 cursor-not-allowed' : ''}`}
                          data-testid="volunteer-workzone-select"
                        >
                          <option value="">Vælg arbejdszone...</option>
                          {filteredWorkZones.map(wz => (
                            <option key={wz.id} value={wz.id}>
                              {wz.label || wz.name}
                              {wz.name !== wz.label && ` (${wz.name})`}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-muted-foreground">
                          💡 Vælg samme arbejdszone som dine venner for at arbejde sammen
                        </p>
                      </>
                    )}
                  </div>
                )}
                
                {/* Vis kort-knap igen */}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowMapPreview(true)}
                  className="w-full"
                >
                  <Map className="h-4 w-4 mr-2" />
                  Se kort
                </Button>
                
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handlePrevStep}
                    className="flex-1"
                  >
                    <ChevronLeft className="h-4 w-4 mr-2" />
                    Tilbage
                  </Button>
                  <Button
                    type="button"
                    onClick={handleNextStep}
                    disabled={!canProceedToStep4}
                    className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
                    data-testid="volunteer-next-step3"
                  >
                    Næste: Log ind
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </>
            )}

            {/* ===== VOLUNTEER STEP 4: Password + Submit ===== */}
            {loginType === "volunteer" && !isRegister && volunteerStep === 4 && (
              <>
                {/* Gæste-velkomst (Plan A) — kun for gæster */}
                {preflightData?.user_type === 'guest' && (
                  <div className="bg-primary/10 border border-primary/30 rounded-lg p-4 mb-4" data-testid="guest-welcome">
                    <h3 className="text-base font-bold mb-1">
                      Hej {preflightData?.name || 'gæst'}, velkommen til Resovia
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Indtast din 6-cifrede gæstekode for at få adgang til{' '}
                      <span className="text-primary font-medium">{events.find(e => e.id === selectedEventId)?.name || 'eventet'}</span>.
                    </p>
                  </div>
                )}

                {/* Opsummering af valg (kun frivillig) */}
                {preflightData?.user_type !== 'guest' && (
                  <div className="bg-zinc-800 rounded-lg p-4 mb-4">
                    <h3 className="text-sm font-bold mb-2">Dine valg:</h3>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Event:</span>
                        <span>{events.find(e => e.id === selectedEventId)?.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Email:</span>
                        <span>{email}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Funktion:</span>
                        <span className="text-primary">{selectedMainRole?.name}</span>
                      </div>
                      {selectedAreaId && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Område:</span>
                          <span>{areas.find(a => a.id === selectedAreaId)?.name}</span>
                        </div>
                      )}
                      {selectedWorkZoneId && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Arbejdszone:</span>
                          <span>{workZones.find(wz => wz.id === selectedWorkZoneId)?.label || workZones.find(wz => wz.id === selectedWorkZoneId)?.name}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {preflightData?.user_type === 'guest' ? (
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-xs uppercase tracking-wider font-mono">
                      Gæstekode
                    </Label>
                    <Input
                      id="password"
                      data-testid="volunteer-password-input"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      placeholder="6-cifret kode"
                      className="bg-zinc-800 border-input focus:border-primary h-10 font-mono"
                    />
                    <p className="text-xs text-muted-foreground">
                      Har du ikke fået en kode, eller er den udløbet? Kontakt administratoren.
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground" data-testid="volunteer-no-password-info">
                    Ingen adgangskode nødvendig — din email er valideret mod frivilliglisten. Tryk på "Log ind" for at fortsætte.
                  </p>
                )}
                
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handlePrevStep}
                    className="flex-1"
                  >
                    <ChevronLeft className="h-4 w-4 mr-2" />
                    Tilbage
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading || (preflightData?.user_type === 'guest' && !password)}
                    className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
                    data-testid="volunteer-login-submit"
                  >
                    {loading ? "Logger ind..." : "Log ind"}
                  </Button>
                </div>
              </>
            )}

            {/* ===== ADMIN/LEDER LOGIN ===== */}
            {loginType === "admin" && !isRegister && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-xs uppercase tracking-wider font-mono">
                    Email
                  </Label>
                  <Input
                    id="email"
                    data-testid="email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="bg-zinc-800 border-input focus:border-primary h-10 font-mono"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-xs uppercase tracking-wider font-mono">
                    Adgangskode
                  </Label>
                  <Input
                    id="password"
                    data-testid="password-input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="bg-zinc-800 border-input focus:border-primary h-10 font-mono"
                  />
                </div>

                <Button
                  data-testid="login-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_10px_rgba(225,255,47,0.2)] font-semibold uppercase tracking-wide text-xs h-10 rounded-md transition-all active:scale-95"
                >
                  {loading ? "Vent..." : "Log ind"}
                </Button>
              </>
            )}

            {/* ===== REGISTRERING ===== */}
            {isRegister && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-xs uppercase tracking-wider font-mono">
                    Navn
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="bg-zinc-800 border-input h-10 font-mono"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-xs uppercase tracking-wider font-mono">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="bg-zinc-800 border-input h-10 font-mono"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-xs uppercase tracking-wider font-mono">
                    Adgangskode
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="bg-zinc-800 border-input h-10 font-mono"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="role" className="text-xs uppercase tracking-wider font-mono">
                    Rolle
                  </Label>
                  <select
                    id="role"
                    value={role}
                    onChange={(e) => {
                      setRole(e.target.value);
                      if (e.target.value !== 'sweeper') setSweeperTeamId('');
                    }}
                    className="flex h-10 w-full rounded-md border border-input bg-zinc-800 px-3 py-2 text-sm font-mono text-white"
                  >
                    <option value="frivillig">Frivillig</option>
                    <option value="admin">Admin</option>
                    <option value="heart_leader">❤️ Heart Leader</option>
                    <option value="chauffør">Chauffør</option>
                    <option value="sweeper">Sweeper</option>
                  </select>
                </div>

                {role === 'sweeper' && (
                  <div className="space-y-2">
                    <Label htmlFor="sweeperTeam" className="text-xs uppercase tracking-wider font-mono">
                      Sweeper-hold
                    </Label>
                    <select
                      id="sweeperTeam"
                      value={sweeperTeamId}
                      onChange={(e) => setSweeperTeamId(e.target.value)}
                      required
                      className="flex h-10 w-full rounded-md border border-input bg-zinc-800 px-3 py-2 text-sm font-mono text-white"
                    >
                      <option value="">Vælg hold...</option>
                      {sweeperTeams.map(team => (
                        <option key={team.id} value={team.id}>{team.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-10"
                >
                  {loading ? "Opretter..." : "Opret bruger"}
                </Button>
              </>
            )}
          </form>

          <div className="mt-6 text-center space-y-3">
            {loginType === "admin" && (
              <button
                onClick={() => setIsRegister(!isRegister)}
                className="text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                {isRegister ? "Har du allerede en konto? Log ind" : "Ingen konto? Opret en"}
              </button>
            )}
            
            {!isRegister && (
              <div>
                <Link 
                  to="/glemt-adgangskode"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  Glemt adgangskode?
                </Link>
              </div>
            )}
          </div>
          </div>
          
          {/* Powered by — inde i boksen på mørk baggrund */}
          {platformSettings?.powered_by_enabled && (
            <div className="relative z-10 border-t border-white/5 px-8 py-3 bg-black/40" data-testid="powered-by-section">
              <div className="flex items-center justify-center gap-2 text-xs text-white/35">
                {platformSettings.host_logo_url && (
                  <img 
                    src={`${API.replace('/api', '')}${platformSettings.host_logo_url}`} 
                    alt={platformSettings.host_name} 
                    className="h-4 w-auto opacity-50" 
                  />
                )}
                <span>
                  {platformSettings.powered_by_text || 'powered by'}{' '}
                  <span className="font-semibold text-white/45">{platformSettings.host_name}</span>
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
